<template>
    <div class="rounded-xl bg-light-gray px-5 pb-5 pt-4 shadow-lg md:min-h-[29rem] lg:px-7 lg:pb-7">
        <transition name="fade" mode="out-in">
            <form @submit.prevent="submit" @input="clear" v-if="!generatedMessage">
                <div class="space-y-4">
                    <FormInput
                        id="recipient_name"
                        type="text"
                        label="Recipient name"
                        :error="errors.recipient_name"
                        maxlength="50"
                        v-model="recipientName"
                    />

                    <ThankboxOccasionSelect
                        v-model="occasion"
                        :allow-searching="false"
                        occasion-list="message_generation"
                    >
                        <template v-if="errors.occasion" #errors>
                            <FormError :error="errors.occasion" class="mt-1" />
                        </template>
                    </ThankboxOccasionSelect>

                    <div class="field">
                        <label for="occasion">Message tone</label>
                        <div class="control flex flex-wrap gap-2.5 text-dark-gray">
                            <button
                                v-for="toneOption in toneOptions"
                                :key="toneOption.value"
                                class="rounded-full border px-4 py-2 transition-colors hover:border-white hover:bg-purple hover:text-white sm:text-lg"
                                :class="{
                                    'border-white bg-purple text-white': tone?.value === toneOption.value,
                                    'border-platinum bg-white': tone?.value !== toneOption.value,
                                }"
                                @click.prevent="tone = toneOption"
                                v-html="toneOption.title"
                            ></button>
                        </div>
                    </div>
                </div>

                <button
                    class="button image mt-5 w-full"
                    type="submit"
                    :disabled="submitting || !formReady"
                    :class="{ loading: submitting }"
                >
                    <i class="fa-solid fa-bolt"></i>
                    <span>Generate message</span>
                </button>

                <p class="mt-3 text-sm text-red-500" v-if="error">
                    {{ error }}
                </p>
            </form>
            <!-- TODO: Add transitions -->
            <div v-else-if="generatedMessage" class="flex flex-col items-center max-md:min-h-[30rem] md:min-h-[28rem]">
                <div
                    class="relative mt-auto w-64 rounded-xl bg-white p-4 text-dark-gray drop-shadow-xl lg:w-80 lg:text-lg"
                >
                    <p>
                        {{ generatedMessage }}
                    </p>
                    <img
                        src="/img/decorations/purple-splash-left.svg"
                        width="30"
                        height="22"
                        alt=""
                        class="absolute -left-8 -top-2 lg:-left-10"
                    />
                    <img
                        src="/img/decorations/purple-splash-bottom-left.svg"
                        width="31"
                        height="27"
                        alt=""
                        class="absolute -bottom-6 -right-9 md:-right-10 lg:-bottom-8 lg:-right-12"
                    />
                    <img
                        src="/img/decorations/shape-cluster-long-0.svg"
                        width="31"
                        height="215"
                        alt=""
                        class="absolute -left-10 bottom-0 h-36 lg:-left-12"
                    />
                    <img
                        src="/img/decorations/star-and-shapes-2.svg"
                        width="50"
                        height="89"
                        alt=""
                        class="absolute inset-y-0 -right-8 my-auto h-auto w-6 sm:-right-10 sm:w-8 lg:right-[-4.5rem] lg:w-auto"
                    />
                </div>
                <div class="mt-7 flex w-64 justify-between px-4 md:mt-5 lg:w-80">
                    <button
                        class="text-button small remove-underline space-x-1 font-bold"
                        v-if="clipboard.isSupported.value"
                        @click="clipboard.copy(generatedMessage)"
                    >
                        <i class="fa-solid fa-copy"></i>
                        <span>{{ clipboard.copied.value ? 'Copied!' : 'Copy' }}</span>
                    </button>
                    <button class="text-button small remove-underline space-x-1 font-bold" @click="restart">
                        <i class="fa-solid fa-history"></i>
                        <span>Retry</span>
                    </button>
                </div>
                <p class="mt-auto pt-4 text-center font-bold text-purple">
                    <template v-if="tone?.value === 'angry'">
                        😳 &nbsp;This message would look brutal in a Thankbox!
                    </template>
                    <template v-else> ✨ &nbsp;This message would look great in a Thankbox!</template>
                </p>
                <a
                    class="button mb-auto mt-4"
                    :href="`/app/thankbox/create?occasion_id=${occasion.id}`"
                    target="_blank"
                >
                    Try it now
                </a>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import FormInput from '@/components/FormInput.vue'
import axios from '@/util/axios'
import isEmpty from '@/util/isEmpty'
import FormError from '@/components/FormError.vue'
import ThankboxOccasionSelect from '@/components/ThankboxOccasionSelect.vue'
import { ThankboxOccasion } from 'resources/js/types'
import trackFathomEvent from '@/util/trackFathomEvent'
import { AxiosError } from 'axios'
import { useClipboard } from '@vueuse/core'

interface ToneOption {
    title: string
    value: string
}

const toneOptions: ToneOption[] = [
    { title: '😆 &nbsp;Funny', value: 'funny' },
    { title: '😠 &nbsp;Angry', value: 'angry' },
    { title: '🌟 &nbsp;Inspirational', value: 'inspirational' },
    { title: '🙄 &nbsp;Sarcastic', value: 'sarcastic' },
    { title: '🧐 &nbsp;Formal', value: 'formal' },
    { title: '🤗 &nbsp;Supportive', value: 'supportive' },
]

const recipientName = ref('')
const occasion = ref<ThankboxOccasion | null>(null)
const tone = ref<ToneOption | null>(null)

const submitting = ref(false)
const generatedMessage = ref<string | null>(null)

const errors = ref<any>({})
const error = ref<string>('')

const clipboard = useClipboard({ source: generatedMessage, legacy: true })

const clear = () => {
    errors.value = {}
}

const formReady = computed<boolean>(
    () => recipientName.value.length > 1 && occasion.value !== null && tone.value !== null,
)

const restart = () => {
    generatedMessage.value = null
    error.value = ''
    clipboard.copied.value = false
}

const submit = async () => {
    if (submitting.value) return

    trackFathomEvent('Generate AI Message')

    submitting.value = true

    axios
        .post('/generate-card-message', {
            recipient_name: recipientName.value,
            tone: tone.value?.value,
            occasion: occasion.value?.title,
        })
        .then(response => {
            generatedMessage.value = response.data.message
        })
        .catch((e: AxiosError<any>) => {
            errors.value = e.response?.data?.errors || {}

            if (isEmpty(errors.value)) {
                if (e.response?.status === 429) {
                    error.value =
                        'You’ve reached the limit of number of messages you can generate. Please try again later.'
                } else {
                    error.value =
                        e.response?.data?.message || 'An error occurred when submitting the form. Please try again.'
                }
            }
        })
        .finally(() => {
            submitting.value = false
        })
}
</script>
